import { useNavigate } from '@tanstack/react-router'
import { Button } from '@trellis/roots/button'
import { DetailPageSkeleton } from '@trellis/roots/loading'
import { PageHeader } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import { toast } from 'sonner'

import {
  useCommitImportMutation,
  useDeleteImportMutation,
  useDisclosure,
  useImportJobQuery,
  useRollbackImportMutation,
} from '@/hooks'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'

import { IMPORT_PARENT_PATHS } from '../import.constants'
import { ImportBackLink } from '../ImportBackLink'
import { ImportCommitDialog } from '../ImportCommitDialog'
import { ImportJobOverview } from '../ImportJobOverview'
import { ImportRollbackDialog } from '../ImportRollbackDialog'
import type { ImportBasePath, ImportPreviewConfig } from '../types'

interface ImportDetailProps<TRow> extends ImportPreviewConfig<TRow> {
  jobId: string
  basePath: ImportBasePath
  entityLabel: string
}

export function ImportDetail<TRow>({
  jobId,
  basePath,
  entityLabel,
  previewColumns,
  getPreviewRows,
}: ImportDetailProps<TRow>) {
  const navigate = useNavigate()
  const commitDialog = useDisclosure()
  const rollbackDialog = useDisclosure()
  const deleteDialog = useDisclosure()

  const { data: importResult, isLoading } = useImportJobQuery(jobId)
  const commitMutation = useCommitImportMutation()
  const rollbackMutation = useRollbackImportMutation()
  const deleteMutation = useDeleteImportMutation()

  useSetBreadcrumbs([
    { label: entityLabel, href: IMPORT_PARENT_PATHS[basePath] },
    { label: 'Import', href: basePath },
    { label: 'Details' },
  ])

  if (isLoading) {
    return <DetailPageSkeleton maxWidth="full" />
  }

  const job = importResult?.data
  if (!job) {
    return <div className="text-muted-foreground p-8 text-sm">Import job not found.</div>
  }

  function handleCommit() {
    commitMutation.mutate(jobId, {
      onSuccess: (result) => {
        commitDialog.close()
        const counts = result.data!.createdCounts
        const summary = Object.entries(counts)
          .filter(([, count]) => count && count > 0)
          .map(([entity, count]) => `${count} ${entity}`)
          .join(', ')
        toast.success('Import committed', {
          description: summary || `${result.data!.importedRows} records imported`,
        })
      },
      onError: (commitError) => {
        commitDialog.close()
        toast.error('Commit failed', { description: commitError.message })
      },
    })
  }

  function handleRollback() {
    rollbackMutation.mutate(jobId, {
      onSuccess: () => {
        rollbackDialog.close()
        toast.success('Import rolled back')
      },
      onError: (rollbackError) => {
        rollbackDialog.close()
        toast.error('Rollback failed', { description: rollbackError.message })
      },
    })
  }

  function handleDelete() {
    deleteMutation.mutate(jobId, {
      onSuccess: () => {
        void navigate({ to: basePath })
      },
      onError: (deleteError) => {
        deleteDialog.close()
        toast.error('Delete failed', { description: deleteError.message })
      },
    })
  }

  const headerActions =
    job.status === 'VALIDATED' ? (
      <>
        <Button
          variant="outline"
          onClick={() => deleteDialog.open(true)}
          className="text-destructive-text hover:text-destructive-text"
        >
          Delete
        </Button>
        <Button onClick={() => commitDialog.open(true)} disabled={job.validRows === 0}>
          Import {job.validRows} records
        </Button>
      </>
    ) : job.status === 'COMMITTED' ? (
      <Button variant="outline" onClick={() => rollbackDialog.open(true)}>
        Roll Back
      </Button>
    ) : undefined

  return (
    <div className="p-8">
      <ImportBackLink
        to={basePath}
        label={`Back to ${entityLabel.toLowerCase()} import`}
      />
      <PageHeader
        title="Import Details"
        subtitle={job.fileName}
        actions={headerActions}
      />

      <ImportJobOverview
        job={job}
        previewColumns={previewColumns}
        getPreviewRows={getPreviewRows}
      />

      <ImportCommitDialog
        open={commitDialog.isOpen}
        onOpenChange={commitDialog.onOpenChange}
        validRows={job.validRows}
        errorRows={job.errorRows}
        onConfirm={handleCommit}
        isPending={commitMutation.isPending}
      />

      <ImportRollbackDialog
        open={rollbackDialog.isOpen}
        onOpenChange={rollbackDialog.onOpenChange}
        importedRows={job.importedRows}
        onConfirm={handleRollback}
        isPending={rollbackMutation.isPending}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={deleteDialog.onOpenChange}
        title="Delete import job?"
        description={
          <>
            This will permanently delete the import job for{' '}
            <strong>{job.fileName}</strong>. No data has been imported.
          </>
        }
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  )
}
